import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import Button from '../../components/atoms/Button';
import Tooltip from '../../components/molecules/Tooltip';
import TextField from '../../components/molecules/TextField';
import { PROMPT_MAX_LENGTH, TRANSLATION_KEY } from '../constants';
import { formatShortScheduleLabel } from '../utils';
import css from './AddMonitorBar.css';

const AddMonitorBar = ({
    prompt,
    onPromptChange,
    onSubmit,
    scheduleCron,
    onScheduleClick,
    submitting = false,
    error = null,
}) => {
    const hasPrompt = Boolean(prompt.trim());

    const submit = () => {
        if (hasPrompt && !submitting) onSubmit(prompt.trim());
    };

    const placeholderKey = 'ADD_BAR.PLACEHOLDER';
    const addKey = 'ADD_BAR.ADD';
    const { label: scheduleLabel, title: scheduleTitle } = formatShortScheduleLabel(scheduleCron);

    const addMonitorButton = (
        <Button
            Icon="plus"
            disabled={!hasPrompt || submitting}
            showSpinner={submitting}
            onClick={submit}
            className={css.addMonitorButton}
        >
            <Translate id={`${TRANSLATION_KEY}.${addKey}`} />
        </Button>
    );

    // Only rendered once the user has typed: an empty composer has nothing to schedule yet.
    const scheduleButton = (
        <Tooltip title={scheduleTitle}>
            <Button
                type="flat"
                className={css.scheduleRow}
                Icon="clock"
                iconSize={IconSizes.XX_SMALL}
                iconClass={css.scheduleIcon}
                onClick={onScheduleClick}
            >
                {scheduleLabel}
                <Icon
                    name="pencil"
                    size={IconSizes.XX_SMALL}
                    interactive={false}
                    className={classNames(css.scheduleIcon, css.schedulePencil)}
                />
            </Button>
        </Tooltip>
    );

    return (
        <div className={css.container}>
            <div className={classNames(css.bar, { [css.expanded]: hasPrompt })}>
                <div className={css.inputRow}>
                    <Icon name="sparkles" size={IconSizes.X_SMALL} interactive={false} className={css.sparkle} />
                    <TextField
                        type="textarea"
                        value={prompt}
                        onChange={onPromptChange}
                        maxLength={PROMPT_MAX_LENGTH}
                        placeholder={`${TRANSLATION_KEY}.${placeholderKey}`}
                        containerClass={css.inputField}
                        inputClassName={css.input}
                        inputConfig={{ rows: 1, autoFocus: true }}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                submit();
                            }
                        }}
                    />
                    {!hasPrompt && addMonitorButton}
                </div>
                {hasPrompt && (
                    <div className={css.footer}>
                        <div className={css.footerActions}>
                            {scheduleButton}
                            {addMonitorButton}
                        </div>
                    </div>
                )}
            </div>
            {error && <div className={css.error}>{error}</div>}
        </div>
    );
};

AddMonitorBar.propTypes = {
    prompt: PropTypes.string.isRequired,
    onPromptChange: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    scheduleCron: PropTypes.string.isRequired,
    onScheduleClick: PropTypes.func.isRequired,
    submitting: PropTypes.bool,
    error: PropTypes.string,
};

export default AddMonitorBar;
